import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearch } from 'src/hooks/use-search';
import { editPresenceZonePromise, type EditPresenceZonePromiseParam } from 'src/utils/api/edit-presence-zone';
import { QUERY_KEY_MAP } from 'src/constants';
import { clearShapesFn } from 'src/components/ZoneEditor/context';
import { useNotification } from 'src/hooks';

export const useEditPresenceZone = () => {
  const queryClient = useQueryClient();
  const { enqueueSuccessNotification, enqueueErrorNotification } = useNotification();

  const [searchParams] = useSearch();
  const deviceId = searchParams?.device?.id || '';

  const { mutateAsync, isPending, isError, error } = useMutation({
    mutationKey: [QUERY_KEY_MAP.useEditPresenceZone],
    mutationFn: editPresenceZonePromise,
  });

  const editPresenceZoneAsync = async (data: EditPresenceZonePromiseParam) => {
    try {
      const result = await mutateAsync(data);

      if (result.success) {
        clearShapesFn();
        enqueueSuccessNotification('saved.presence.zone');

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_MAP.useGetZoneLastUpdatedMeta, deviceId],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_MAP.useGetPresenceZoneData, deviceId],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_MAP.useGetDeviceZoneVersionHistory, deviceId],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_MAP.useGetPresenceZonesByDeviceIds],
          }),
        ]);

        return result;
      }

      enqueueErrorNotification(result.message);
      return result;
    } catch (e) {
      console.error(e);
      enqueueErrorNotification((e as Error).message || 'Something went wrong');
      return {
        success: false,
        message: (e as Error).message || 'Something went wrong',
      };
    }
  };

  return {
    editPresenceZoneAsync,
    isLoading: isPending,
    isError,
    error,
  };
};
